import { useEffect, useRef } from 'react';
import { MessageBubble } from './MessageBubble';
import { ChatInput } from './ChatInput';

interface ChatMessage {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderRole: 'staff' | 'family' | 'system';
  content: string;
  messageType: string;
  createdAt: string;
}

interface ChatPanelProps {
  conversationTitle: string;
  messages: ChatMessage[];
  currentUserId: string;
  loading: boolean;
  hasMore: boolean;
  canSendUrgent?: boolean;
  onSend: (content: string, messageType?: string) => Promise<void>;
  onLoadMore: () => Promise<void>;
  onMarkRead: () => Promise<void>;
  onBack?: () => void;
}

export function ChatPanel({
  conversationTitle,
  messages,
  currentUserId,
  loading,
  hasMore,
  canSendUrgent = false,
  onSend,
  onLoadMore,
  onMarkRead,
  onBack,
}: ChatPanelProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevMessageCount = useRef(0);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messages.length > prevMessageCount.current) {
      // Only auto-scroll if we were near the bottom or this is a fresh load
      const container = containerRef.current;
      if (container) {
        const isNearBottom =
          container.scrollHeight - container.scrollTop - container.clientHeight < 100;
        if (isNearBottom || prevMessageCount.current === 0) {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
    prevMessageCount.current = messages.length;
  }, [messages.length]);

  // Mark as read when viewing messages
  useEffect(() => {
    if (messages.length > 0) {
      onMarkRead();
    }
  }, [messages.length, onMarkRead]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b bg-white px-4 py-3 flex items-center gap-3 shrink-0">
        {onBack && (
          <button
            onClick={onBack}
            className="md:hidden p-1 -ml-1 text-gray-500 hover:text-gray-700"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        <h2 className="text-sm font-semibold text-gray-900 truncate">{conversationTitle}</h2>
      </div>

      {/* Messages */}
      <div ref={containerRef} className="flex-1 overflow-y-auto px-4 py-3">
        {hasMore && (
          <div className="text-center mb-4">
            <button
              onClick={onLoadMore}
              disabled={loading}
              className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400"
            >
              {loading ? 'Loading...' : 'Load older messages'}
            </button>
          </div>
        )}

        {loading && messages.length === 0 ? (
          <div className="text-center text-gray-400 text-sm py-8">Loading messages...</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-400 text-sm py-8">
            No messages yet. Start the conversation!
          </div>
        ) : (
          messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              isOwn={msg.senderId === currentUserId}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <ChatInput onSend={onSend} canSendUrgent={canSendUrgent} />
    </div>
  );
}
